import { useEffect, useState } from "react"

import { RolesPermissionsCard } from "@/components/RolesPermissionsCard"
import { getOtkRoles, getOtkUsers, type OtkRole, type OtkUser } from "@/lib/api/users"
import { useIsAdmin } from "@/store/session"

/**
 * subject-roles.md's `/subject/roles`.
 *
 * Roles/permissions are read-only per the spec regardless of viewer — no
 * write endpoint is proposed for `GET /api/otk/roles` and none exists in
 * `otk-users.ts`. `RestrictedPaymentNotice` is intentionally not wired here
 * for the same reason `SubjectProfile.tsx`/`SubjectUsers.tsx` omit it: no
 * payment/activation endpoint is reachable from this subject yet.
 *
 * The permission matrix (`GET /otk/users/roles`) is visible to every role,
 * but the per-role user lists (`RolesPermissionsCard`'s collapsible
 * "Ролі в АРМ" cards) are Адмін-only, same as `/subject/users` — and
 * `GET /otk/users` itself requires `MANAGE_USERS_ROLES_INVITES` server-side
 * (otk-users.ts), so it's only fetched for an admin viewer in the first
 * place rather than fetched and then hidden.
 */
export function SubjectRoles() {
  const isAdmin = useIsAdmin()
  const [roleRows, setRoleRows] = useState<OtkRole[] | null>(null)
  const [users, setUsers] = useState<OtkUser[]>([])
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    Promise.all([getOtkRoles(), isAdmin ? getOtkUsers() : Promise.resolve([])])
      .then(([roles, usersData]) => {
        if (cancelled) return
        setRoleRows(roles)
        setUsers(usersData)
      })
      .catch((err) => {
        if (cancelled) return
        const code = err?.response?.data?.error as string | undefined
        setError(
          code === "SUBJECT_NOT_CONNECTED"
            ? "Обліковий запис ще не пов'язаний із суб'єктом ОТК."
            : "Не вдалося завантажити ролі в АРМ."
        )
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const roleLabelById = Object.fromEntries(
    (roleRows ?? []).map((role) => [role.id, role.name])
  )

  return (
    <>
      <h1 className="mb-6 text-2xl font-semibold">Суб'єкт ОТК — Ролі в АРМ</h1>

      {isLoading && (
        <p className="text-sm text-muted-foreground">Завантаження…</p>
      )}
      {!isLoading && error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      {!isLoading && !error && roleRows && (
        <RolesPermissionsCard
          roleLabelById={roleLabelById}
          roleRows={roleRows}
          users={users}
        />
      )}
    </>
  )
}
